import { DateTime } from 'luxon';
import type {
  INovaAppointment,
  INovaCarrier,
  INovaCompany,
  INovaDock,
  INovaWarehouse
} from '@/interfaces/nova';
import type { NovaApiService } from '@/services/nova/NovaApiService';
import { cleanWhiteSpace, emailToFormattedArray } from '@/services/util';
import {
  ALREADY_EXISTS_MESSAGE,
  SUCCESS_IMPORT_MESSAGE,
  forEachSequentially,
  getErrorMessage,
  getFailureStatusMessage,
  wasAlreadyImported
} from './importShared';

type AppointmentImportParams = {
  orgId: string;
  appointments: INovaAppointment[];
  apiService: NovaApiService;
  companiesKeyedByName: Record<string, INovaCompany>;
  loadTypeIdsKeyedByExternalId: { [key: string]: string };
  warehousesKeyedByExternalId: { [key: string]: INovaWarehouse };
  docksKeyedByExternalId: { [key: string]: INovaDock };
};

type AppointmentImportState = Omit<AppointmentImportParams, 'appointments'> & {
  carriersKeyedByEmail: Record<string, INovaCarrier>;
  appointmentsReport: INovaAppointment[];
};

type AppointmentKind = { isStandard: boolean; isReserve: boolean };

function getAppointmentCcEmails(appointment: INovaAppointment, appointmentEmails: string[]) {
  return [...appointmentEmails.slice(1), ...emailToFormattedArray(appointment.ccEmails ?? [])];
}

function reportFailure(
  appointment: INovaAppointment,
  message: string,
  { appointmentsReport }: AppointmentImportState
) {
  appointment.wasImportedIntoNova = getFailureStatusMessage({ message });
  appointmentsReport.unshift(appointment);
}

function getValidationFailure(
  appointment: INovaAppointment,
  { isStandard }: AppointmentKind,
  loadTypeIdsKeyedByExternalId: { [key: string]: string }
) {
  if (!appointment.externalCompanyName && isStandard) {
    return 'Appointment is missing company name';
  }
  if (!appointment.email && isStandard) {
    return 'Appointment is missing carrier email';
  }
  if (!appointment.firstName) {
    return 'Appointment is missing carrier first name';
  }
  if (!loadTypeIdsKeyedByExternalId[appointment.externalLoadTypeId] && isStandard) {
    return `Appointment's Load Type (${appointment.externalLoadTypeId}) failed to import - see LoadTypes tab for details`;
  }
  return null;
}

/** Finds or creates the appointment's company; a thrown error means the creation failed. */
async function resolveCompany(
  appointment: INovaAppointment,
  { apiService, companiesKeyedByName }: AppointmentImportState
): Promise<INovaCompany> {
  const companyName = appointment.externalCompanyName as string;
  let company = companiesKeyedByName[companyName];
  if (!company) {
    const companyRes = await apiService.createCompany(companyName);
    const [createdCompany] = companyRes.data ?? [];
    if (!createdCompany) {
      throw new Error('no company was returned');
    }
    company = createdCompany;
    companiesKeyedByName[companyName] = company;
  }
  return company;
}

async function loadCarrierByEmail(
  appointment: INovaAppointment,
  { apiService, carriersKeyedByEmail }: AppointmentImportState
) {
  if (carriersKeyedByEmail[appointment.email]) {
    return;
  }
  const carrierResponse = await apiService.getCarriers({
    s: {
      email: {
        $eq: appointment.email
      }
    }
  });
  const [existingCarrier] = carrierResponse?.data ?? [];
  if (existingCarrier) {
    carriersKeyedByEmail[appointment.email] = existingCarrier;
  }
}

async function createCarrierForAppointment(
  appointment: INovaAppointment,
  company: INovaCompany,
  { apiService, carriersKeyedByEmail }: AppointmentImportState
) {
  const carrierData = {
    email: appointment.email,
    firstName: appointment.firstName ?? 'NO_FIRST_NAME',
    lastName: appointment.lastName ?? 'NO_LAST_NAME',
    companyId: company.id,
    phone: appointment.phone
  };
  if (!appointment.phone) {
    delete carrierData.phone;
  }
  const createCarrierRes = await apiService.createCarrier(carrierData);
  const [carrier] = createCarrierRes?.data ?? [];
  if (!carrier) {
    throw new Error('no carrier was returned');
  }
  carriersKeyedByEmail[appointment.email.toLowerCase()] = carrier;
  return carrier;
}

function joinCcEmails(appointment: INovaAppointment) {
  return Array.isArray(appointment.ccEmails) ? appointment.ccEmails.join(', ') : '';
}

function joinAppointmentTags(appointment: INovaAppointment) {
  return Array.isArray(appointment.tags) ? appointment.tags.join(', ') : appointment.tags;
}

async function createAppointmentRecord(
  appointment: INovaAppointment,
  carrier: INovaCarrier | undefined,
  { isStandard, isReserve }: AppointmentKind,
  importState: AppointmentImportState
) {
  const { orgId, apiService, loadTypeIdsKeyedByExternalId, docksKeyedByExternalId } = importState;
  const { warehousesKeyedByExternalId, appointmentsReport } = importState;
  const appointmentWarehouse = warehousesKeyedByExternalId[appointment.externalWarehouseId];
  if (!appointmentWarehouse) {
    reportFailure(
      appointment,
      `Warehouse (${appointment.externalWarehouseId}) failed to import.  See Warehouses tab for details`,
      importState
    );
    return;
  }
  const appointmentDock = docksKeyedByExternalId[appointment.externalDockId];
  if (!appointmentDock?.id) {
    reportFailure(
      appointment,
      `Dock (${appointment.externalDockId}) was not imported - see Docks tab for details`,
      importState
    );
    appointment.ccEmails = joinCcEmails(appointment);
    return;
  }
  if (appointment.tags && typeof appointment.tags === 'string') {
    appointment.tags = Array.from(
      new Set(appointment.tags.split(',').map(tag => cleanWhiteSpace(tag)))
    );
  }
  const dateOpts = { zone: appointmentWarehouse.timezone };
  const appointmentData = {
    ...appointment,
    status: appointment.status?.toLowerCase() === 'requested' ? 'Requested' : 'Scheduled',
    userId: isStandard ? carrier?.id : appointment.userId,
    loadTypeId: isStandard ? loadTypeIdsKeyedByExternalId[appointment.externalLoadTypeId] : null,
    dockId: appointmentDock.id,
    orgId,
    start: DateTime.fromISO(appointment.start as string, dateOpts)
      .toUTC()
      .toISO(),
    end: DateTime.fromISO(appointment.end as string, dateOpts)
      .toUTC()
      .toISO(),
    customFields: appointment.customFields ? JSON.parse(appointment.customFields as string) : null
  };
  try {
    const appointmentResponse = isReserve
      ? await apiService.createReserve(appointmentData)
      : await apiService.createAppointment(appointmentData);
    appointment.id = appointmentResponse?.data?.[0]?.id ?? '';
    appointment.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
    appointment.ccEmails = joinCcEmails(appointment);
    appointment.tags = joinAppointmentTags(appointment);
    appointmentsReport.push(appointment);
  } catch (e) {
    appointment.wasImportedIntoNova = getFailureStatusMessage({ e });
    appointment.ccEmails = joinCcEmails(appointment);
    appointment.tags = joinAppointmentTags(appointment);
    appointmentsReport.unshift(appointment);
  }
}

async function importAppointment(
  appointment: INovaAppointment,
  importState: AppointmentImportState
) {
  const rawType = (appointment.type as string)?.toLowerCase();
  const kind: AppointmentKind = {
    isStandard: !rawType || rawType === 'standard',
    isReserve: rawType === 'reserve'
  };

  if (wasAlreadyImported(appointment)) {
    appointment.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
    importState.appointmentsReport.push(appointment);
    return;
  }
  const validationFailure = getValidationFailure(
    appointment,
    kind,
    importState.loadTypeIdsKeyedByExternalId
  );
  if (validationFailure) {
    reportFailure(appointment, validationFailure, importState);
    return;
  }

  let company: INovaCompany | undefined;
  if (kind.isStandard) {
    try {
      company = await resolveCompany(appointment, importState);
    } catch (e) {
      reportFailure(
        appointment,
        `appointment company creation failed because ${getErrorMessage(e)}`,
        importState
      );
      return;
    }
  }

  const appointmentEmails = Array.from(new Set(emailToFormattedArray(appointment.email)));
  appointment.email = appointmentEmails[0] ?? '';
  appointment.ccEmails = getAppointmentCcEmails(appointment, appointmentEmails);

  if (kind.isStandard) {
    await loadCarrierByEmail(appointment, importState);
  }

  let carrier = importState.carriersKeyedByEmail[appointment.email];
  if (!carrier && company) {
    try {
      carrier = await createCarrierForAppointment(appointment, company, importState);
    } catch (e) {
      const reason = getErrorMessage(e).includes('Duplicate key')
        ? 'email already exists as a non-carrier'
        : getErrorMessage(e);
      reportFailure(
        appointment,
        `appointment carrier creation failed because ${reason}`,
        importState
      );
      return;
    }
  }

  try {
    await createAppointmentRecord(appointment, carrier, kind, importState);
  } catch (e) {
    appointment.wasImportedIntoNova = getFailureStatusMessage({ e });
    importState.appointmentsReport.unshift(appointment);
  }
}

export async function importAppointments({
  appointments,
  ...sharedParams
}: AppointmentImportParams): Promise<INovaAppointment[]> {
  const importState: AppointmentImportState = {
    ...sharedParams,
    carriersKeyedByEmail: {},
    appointmentsReport: []
  };
  await forEachSequentially(appointments, appointment =>
    importAppointment(appointment, importState)
  );
  return importState.appointmentsReport;
}
